import { useState, useEffect, useCallback } from 'react';
import type { HeaderFeedResponse, WeatherLocation } from '@/types/headerFeed';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'geniebot_weather_location';
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function useHeaderFeed() {
  const [data, setData] = useState<HeaderFeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weatherLocation, setWeatherLocationState] = useState<WeatherLocation | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const fetchFeed = useCallback(async (location?: WeatherLocation | null) => {
    try {
      setLoading(true);
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      
      const loc = location ?? weatherLocation;
      const params = new URLSearchParams();
      
      if (loc?.lat && loc?.lon) {
        params.set('lat', String(loc.lat));
        params.set('lon', String(loc.lon));
        // For display purposes only, don't duplicate with zip
        if (loc.city && loc.city !== 'Current Location') {
          params.set('location', loc.city + (loc.state ? `, ${loc.state}` : ''));
        }
      } else if (loc?.zip) {
        // ZIP code only - send just the zip
        params.set('location', loc.zip);
      } else if (loc?.city) {
        // City/state without zip
        params.set('location', loc.city + (loc.state ? `, ${loc.state}` : ''));
      }
      
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/header-feed?${params.toString()}`;
      
      let res: Response;
      try {
        res = await fetch(url, {
          signal: AbortSignal.timeout(30000),
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            'Content-Type': 'application/json',
          },
        });
      } catch (networkError) {
        // Network-level errors (offline, connection reset, preview proxy) - silently degrade
        console.warn('Header feed network error:', networkError);
        setLoading(false);
        return;
      }
      
      // Handle non-OK responses gracefully
      if (!res.ok) {
        // Try to parse error message
        try {
          const errorData = await res.json();
          // Offline or server errors - don't crash, just log
          if (errorData.error === 'offline' || res.status === 503 || res.status === 500) {
            console.warn('Header feed unavailable:', errorData.message || res.status);
            setError('Feed temporarily unavailable');
            setLoading(false);
            return;
          }
        } catch {
          // Couldn't parse error JSON
        }
        console.warn(`Header feed error: ${res.status}`);
        setError('Failed to load feed');
        setLoading(false);
        return;
      }
      
      let feedData;
      try {
        feedData = await res.json();
      } catch {
        console.warn('Header feed: invalid JSON response');
        setError('Invalid feed data');
        setLoading(false);
        return;
      }
      
      setData(feedData);
    } catch (err) {
      console.error('Header feed error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [weatherLocation]);

  const setWeatherLocation = useCallback((location: WeatherLocation | null) => {
    setWeatherLocationState(location);
    if (location) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    fetchFeed(location);
  }, [fetchFeed]);

  const useCurrentLocation = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const loc: WeatherLocation = {
            city: 'Current Location',
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          };
          setWeatherLocation(loc);
          resolve();
        },
        (err) => {
          reject(new Error(err.message));
        },
        { enableHighAccuracy: false, timeout: 10000 }
      );
    });
  }, [setWeatherLocation]);

  useEffect(() => {
    // Defer initial fetch to avoid blocking first paint
    const initialDelay = setTimeout(() => {
      fetchFeed();
    }, 2000);
    
    const interval = setInterval(() => {
      fetchFeed();
    }, REFRESH_INTERVAL);
    
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [fetchFeed]);

  return {
    data,
    loading,
    error,
    weatherLocation,
    setWeatherLocation,
    useCurrentLocation,
    refresh: fetchFeed,
  };
}
